/*
 * Eager EHR/local hydration for Planner "Recent" encounter rows.
 *
 * Recent rows render language/DOB/MRN from `event.patient`, exactly like the
 * Daily Schedule. The schedule is reliable because usePlannerSchedule bulk
 * fetches every appointment patient from the EHR cache up front
 * (ehrService.fetchCachedPatientsByIds) and attaches it before first paint.
 *
 * Recent encounters are local-keyed (encounter.patient_id) and frequently lack
 * a stored `ehr_patient_id`, so historically they only got demographics through
 * a fragile per-row async hook. This module mirrors the schedule's approach for
 * encounters: resolve EHR + local patient records once per page and attach them
 * as `encounter.ehrPatient` / `encounter.localPatient`, which
 * `plannerPatientFromEncounterView` reads. Best-effort throughout — any failure
 * leaves the encounters untouched so the denormalized row still renders.
 *
 * SCOPE NOTE (schedule migration): the Daily Schedule no longer uses this
 * module — GET /api/v2/views/schedule returns rows with demographics
 * resolved server-side (usePlannerSchedule). What remains live is the
 * paginated encounter-HISTORY surface (usePlannerEncounterHistory ->
 * fetchHydratedUserEncounterList below), which pages /sessions/recent
 * independently of the composed schedule. Delete once history rows also
 * arrive demographics-resolved from a backend view.
 */

import {fetchUserEncounterList} from '../../services/encounterService';
import {
  fetchPlannerLocalPatientsByIds,
  resolvePlannerEhrPatients,
} from './plannerPatientHydrationCache';

const clean = (value: any) => String(value ?? '').trim();

// Strip an optional `Patient/` (FHIR) prefix down to the bare source id so it
// matches the bare ids the EHR cache and appointment patients use.
const normalizeEhrId = (value: any) =>
  clean(value).split('/').filter(Boolean).pop() || '';

const linkedEhrIdFromLocalPatient = (localPatient: Record<string, any> = {}) =>
  normalizeEhrId(
    localPatient.fhir_patient_id ||
      localPatient.ehr_patient_id ||
      localPatient.ehrPatientId ||
      localPatient._ehrPatientId ||
      localPatient._linkedEhrId,
  );

// The EHR source id used to resolve demographics for a single encounter: a
// stored `ehr_patient_id` when present, otherwise the linked local patient's
// FHIR id (bucket B).
const encounterEhrSourceId = (
  encounter: any,
  localPatientsById: Record<string, any>,
) => {
  const direct = normalizeEhrId(encounter?.ehr_patient_id);
  if (direct) return direct;
  const localPatient = localPatientsById[clean(encounter?.patient_id)];
  return localPatient ? linkedEhrIdFromLocalPatient(localPatient) : '';
};

/**
 * Resolve EHR + local demographics for a page of recent encounter views and
 * attach them as `ehrPatient` / `localPatient`. Two buckets:
 *   A) encounters that already carry an `ehr_patient_id`
 *   B) local-only encounters -> fetch the local Patient doc, attach it, and use
 *      its `fhir_patient_id` so it can ALSO pull EHR demographics.
 *
 * Uses ehrService.resolveEhrPatients (cache-first + capped live fallback) rather
 * than fetchCachedPatientsByIds, because recents reach further back than today's
 * cached appointment patients.
 */
export const hydrateEncounterViews = async (
  encounters: any[] = [],
  {companyId = null}: Record<string, any> = {},
) => {
  if (!Array.isArray(encounters) || !encounters.length) return encounters;

  // Bucket B: local ids that need a Patient-doc lookup (no stored EHR id).
  const localIds = [
    ...new Set(
      encounters
        .filter(
          (encounter: any) =>
            !clean(encounter?.ehr_patient_id) && clean(encounter?.patient_id),
        )
        .map((encounter: any) => clean(encounter.patient_id)),
    ),
  ];

  let localPatientsById: Record<string, any> = {};
  if (localIds.length) {
    try {
      localPatientsById = await fetchPlannerLocalPatientsByIds(localIds);
    } catch {
      localPatientsById = {};
    }
  }

  // Bucket A: direct EHR ids + linked EHR ids discovered from the local docs.
  const ehrIds = new Set<string>();
  encounters.forEach((encounter: any) => {
    const id = encounterEhrSourceId(encounter, localPatientsById);
    if (id) ehrIds.add(id);
  });

  let ehrPatientsById: Record<string, any> = {};
  if (companyId && ehrIds.size) {
    try {
      ehrPatientsById = await resolvePlannerEhrPatients(companyId, [...ehrIds]);
    } catch {
      ehrPatientsById = {};
    }
  }

  if (
    !Object.keys(localPatientsById).length &&
    !Object.keys(ehrPatientsById).length
  ) {
    return encounters;
  }

  return encounters.map((encounter: any) => {
    if (!encounter) return encounter;
    const localPatient = localPatientsById[clean(encounter.patient_id)] || null;
    const ehrSourceId = encounterEhrSourceId(encounter, localPatientsById);
    const ehrPatient = ehrSourceId
      ? ehrPatientsById[ehrSourceId] || null
      : null;
    if (!localPatient && !ehrPatient) return encounter;
    return {
      ...encounter,
      ...(localPatient ? {localPatient} : {}),
      ...(ehrPatient ? {ehrPatient} : {}),
    };
  });
};

/**
 * Hydrating drop-in for `fetchUserEncounterList`, used as the planner history
 * `fetchPage`. Preserves the `{encounters, lastDoc, hasMore, ...}` contract so
 * it slots into usePaginatedPlannerEncounterHistory unchanged, but returns rows
 * with EHR/local demographics already attached.
 */
export const fetchHydratedUserEncounterList = async (
  userId: any,
  {companyId = null, ...options}: Record<string, any> = {},
) => {
  // companyId is forwarded so the V2 recents mapper can stamp `company_id`
  // on the EncounterViews (the REST rows are already company-scoped).
  const result = await fetchUserEncounterList(userId, {...options, companyId});
  const encounters = await hydrateEncounterViews(result?.encounters || [], {
    companyId,
  });
  return {...result, encounters};
};
